import * as Sentry from '@sentry/react'
import { isLocal, isProduction, isQc } from 'vars'
import conf from 'conf'
import { enrichFetchFailureEvent, isNoisyEvent } from './filters'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
let initedSentry = false

export const initSentry = () => {
  // 중복 초기화 방지 — 재호출 시 replayIntegration이 중복 등록되어
  // "Multiple Sentry Session Replay instances are not supported" (OFFICEMAIL-WEB-ACCESS-JA) 발생
  if (initedSentry === true) {
    return
  }
  // QC 환경에서는 Sentry를 사용하지 않는다.
  if (isQc === true) {
    return
  }
  // if (!isProduction) {
  //   return
  // }
  if (isLocal === true) {
    return
  }

  const release =
    (isProduction ? '' : 'dev-') +
    import.meta.env.VITE_APP_VERSION +
    '(' +
    conf.gitInfo.commit.hash.substring(0, 7) +
    ')'

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: isProduction ? 'production' : 'development',
    release: release,

    sendDefaultPii: true,

    tracesSampler(_samplingContext) {
      //   console.log('samplingContext - ', _samplingContext)

      return isProduction ? 0.1 : 1.0
    },

    profilesSampleRate: 0.1,

    debug: false,

    replaysOnErrorSampleRate: 1.0,

    replaysSessionSampleRate: 0.0,

    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [window.location.origin],
        networkCaptureBodies: true,
        // 오래 열어둘수록 rrweb mirror/버퍼가 누적되어 동일 DOM 직렬화 비용이 폭증하므로
        // 버퍼 보존 시간을 줄여 누적을 억제한다 (기본 60분 → 10분).
        maxReplayDuration: 10 * 60 * 1000,
      }),

      Sentry.browserTracingIntegration({
        enableLongTask: true,
        enableInp: true, // Interaction to Next Paint (INP)
      }),

      Sentry.browserProfilingIntegration(),

      Sentry.consoleLoggingIntegration({ levels: ['error', 'warn'] }),
    ],

    // Filter out noisy console breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console') {
        return null
      }
      return breadcrumb
    },

    beforeSend(event, hint) {
      if (isNoisyEvent(event, hint)) {
        return null
      }
      enrichFetchFailureEvent(event)
      return event
    },

    maxBreadcrumbs: 50,
    attachStacktrace: true,
    sendClientReports: true,

    enableLogs: true,
  })

  initedSentry = true
}

export const sentrySetUser = (email?: string | null, id?: string | null) => {
  if (initedSentry === false || email == null || email.length === 0) {
    return
  }

  Sentry.setUser({
    id: id || email,
    email: email,
  })
}

// D-3: 토큰 갱신 실패는 TokenRequestFailedError로 noisy drop되어 실패율 관측이 불가능했다.
// 갱신 실패를 warning 레벨 메시지로 별도 기록해 staging/운영에서 갱신 실패율을 추적한다.
export const captureTokenRefreshFailure = (reason?: string) => {
  if (initedSentry === false) {
    return
  }
  Sentry.captureMessage('[JWT] token refresh failed', {
    level: 'warning',
    tags: { area: 'token-refresh' },
    extra: { reason: reason ?? '' },
  })
}

// 비정상 로그아웃(세션 revoke·IP 차단 등 사용자가 의도하지 않은 강제 로그아웃)을 운영에서 추적한다.
// 개발(isLocal)에서는 Sentry가 초기화되지 않아 no-op — 호출부의 console 로그가 개발용 출력을 담당한다.
// 호출부(handleTokenRevoked)는 debounce 통과 후에만 부르므로 세션당 1회 기록된다.
export const captureAbnormalLogout = (kind: string, reason?: string) => {
  if (initedSentry === false) {
    return
  }
  Sentry.captureMessage('[AUTH] abnormal logout', {
    level: 'warning',
    tags: { area: 'auth-logout', kind },
    extra: { reason: reason ?? '' },
  })
}

// OMD-107: 메일 삭제/검색/정렬 변경으로 페이지네이션이 리셋된 뒤 도착한 stale 목록 응답을
// 폐기할 때 breadcrumb를 남긴다. 빈번·저심각도라 이벤트(captureMessage)가 아니라 breadcrumb로
// 기록해 후속 에러 이벤트의 맥락으로만 제공한다. production은 console이 제거되므로 실질 관측 수단.
export const addStaleThreadListDropBreadcrumb = (from: number, to: number) => {
  if (initedSentry === false) {
    return
  }
  Sentry.addBreadcrumb({
    category: 'mailbox',
    level: 'info',
    message: 'stale thread list response dropped',
    data: { from, to },
  })
}
